import { IPublicClientApplication } from '@azure/msal-browser';
import {
  AwardConfigurationRequest,
  AwardLead,
  AwardNominationReviewer,
  AwardRequest,
  NominationRole,
} from '../../models';
import { MoveAwardRequest } from '../../models/move-award-request';
import { ApiEndpointCallStatesStore } from '../api-endpoint-call-states-store';
import { FetchContext } from '../fetch-context';
import { promiseWithObservableState } from '../promise-with-observable-state';
import { withApiAccessToken } from '../with-api-access-token';
import {
  assignAwardLeads,
  assignAwardNominationReviewers,
  createAward,
  deleteAward,
  deleteAwardExecutiveSummaryFileTemplate,
  deleteAwardLeads,
  deleteAwardNominationReviewer,
  getAwardById,
  getAwardExecutiveSummaryFileTemplateMetadata,
  getAwardLeads,
  getAwardNominationRefs,
  getAwardNominationReviewers,
  getAwardNominationRole,
  getAwardWinners,
  getDesignationProgramOptions,
  getEligibleAwardRefs,
  getSpecializationPrograms,
  moveAward,
  updateAward,
  updateAwardConfiguration,
  updateAwardNominationRole,
  uploadAwardExecutiveSummaryFileTemplate,
} from './index';
import { getAwardJudgeCapacityOptions } from './get-award-judge-capacity-options';

export const createAwardsApiEndpoints = (
  fetchContext: FetchContext,
  apiEndpointCallStates: ApiEndpointCallStatesStore,
) => ({
  getAwardById: (instance: IPublicClientApplication, awardId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getAwardById(fetchContext, awardId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getAwardById', isInFlight);
    },
  ),
  getEligibleAwardRefs: (instance: IPublicClientApplication) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getEligibleAwardRefs(fetchContext, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getEligibleAwardRefs', isInFlight);
    },
  ),
  createAward: (instance: IPublicClientApplication, award: AwardRequest) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => createAward(fetchContext, award, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('createAward', isInFlight);
    },
  ),
  updateAward: (instance: IPublicClientApplication, awardId: number, award: AwardRequest) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateAward(fetchContext, awardId, award, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateAward', isInFlight);
    },
  ),
  moveAward: (instance: IPublicClientApplication, awardId: number, moveAwardRequest: MoveAwardRequest) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => moveAward(fetchContext, awardId, moveAwardRequest, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('moveAward', isInFlight);
    },
  ),
  updateAwardConfiguration: (instance: IPublicClientApplication, awardId: number, awardConfiguration: AwardConfigurationRequest) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateAwardConfiguration(fetchContext, awardId, awardConfiguration, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateAwardConfiguration', isInFlight);
    },
  ),
  deleteAward: (instance: IPublicClientApplication, awardId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => deleteAward(fetchContext, awardId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('deleteAward', isInFlight);
    },
  ),
  getAwardJudgeCapacityOptions: (instance: IPublicClientApplication, awardId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getAwardJudgeCapacityOptions(fetchContext, awardId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getAwardJudgeCapacityOptions', isInFlight);
    },
  ),
  getAwardLeads: (instance: IPublicClientApplication, awardId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getAwardLeads(fetchContext, awardId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getAwardLeads', isInFlight);
    },
  ),
  getAwardNominationReviewers: (instance: IPublicClientApplication, awardId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getAwardNominationReviewers(fetchContext, awardId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getAwardNominationReviewers', isInFlight);
    },
  ),
  getSpecializationPrograms: (instance: IPublicClientApplication) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getSpecializationPrograms(fetchContext, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getSpecializationPrograms', isInFlight);
    },
  ),
  getDesignationProgramOptions: (instance: IPublicClientApplication) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getDesignationProgramOptions(fetchContext, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getDesignationProgramOptions', isInFlight);
    },
  ),
  getAwardNominationRole: (instance: IPublicClientApplication, awardId: number, nominationId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getAwardNominationRole(fetchContext, awardId, nominationId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getAwardNominationRole', isInFlight);
    },
  ),
  updateAwardNominationRole: (instance: IPublicClientApplication, awardId: number, nominationId: number, nominationRole: NominationRole) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => updateAwardNominationRole(fetchContext, awardId, nominationId, nominationRole, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('updateAwardNominationRole', isInFlight);
    },
  ),
  getAwardExecutiveSummaryFileTemplateMetadata: (instance: IPublicClientApplication, awardId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getAwardExecutiveSummaryFileTemplateMetadata(fetchContext, awardId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getAwardExecutiveSummaryFileTemplateMetadata', isInFlight);
    },
  ),
  uploadAwardExecutiveSummaryFileTemplate: (instance: IPublicClientApplication, awardId: number, documentContent: File) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => uploadAwardExecutiveSummaryFileTemplate(fetchContext, awardId, documentContent, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('uploadAwardExecutiveSummaryFileTemplate', isInFlight);
    },
  ),
  deleteAwardExecutiveSummaryFileTemplate: (instance: IPublicClientApplication, awardId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => deleteAwardExecutiveSummaryFileTemplate(fetchContext, awardId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('deleteAwardExecutiveSummaryFileTemplate', isInFlight);
    },
  ),
  assignAwardLeads: (instance: IPublicClientApplication, awardId: number, awardLead: AwardLead) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => assignAwardLeads(fetchContext, awardId, awardLead, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('assignAwardLeads', isInFlight);
    },
  ),
  deleteAwardLeads: (instance: IPublicClientApplication, awardLeadId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => deleteAwardLeads(fetchContext, awardLeadId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('deleteAwardLeads', isInFlight);
    },
  ),
  assignAwardNominationReviewers: (instance: IPublicClientApplication, awardId: number, awardNominationReviewer: AwardNominationReviewer) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => assignAwardNominationReviewers(fetchContext, awardId, awardNominationReviewer, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('assignAwardNominationReviewers', isInFlight);
    },
  ),
  deleteAwardNominationReviewer: (instance: IPublicClientApplication, awardNominationReviewerId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => deleteAwardNominationReviewer(fetchContext, awardNominationReviewerId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('deleteAwardNominationReviewer', isInFlight);
    },
  ),
  getAwardNominationRefs: (instance: IPublicClientApplication, awardId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getAwardNominationRefs(fetchContext, awardId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getAwardNominationRefs', isInFlight);
    },
  ),
  getAwardWinners: (instance: IPublicClientApplication, awardId: number) => promiseWithObservableState(
    withApiAccessToken(
      instance,
      (accessToken) => getAwardWinners(fetchContext, awardId, accessToken),
    ),
    (isInFlight) => {
      apiEndpointCallStates.setIsInFlight('getAwardWinners', isInFlight);
    },
  ),
});
